import type {
  CreateIqServiceInventoryInput,
  IqServiceInventoryItem,
  UpdateIqServiceInventoryInput,
} from '@/types/iq-service-inventory';
import type { IqServiceUpsertValues } from './iq-service-inventory-fields';

/**
 * Shared `values → API input` mapping for the IQ-service upsert form. Lifted out of
 * `iq-service-inventory-library.tsx` so the per-merchant dialog AND the platform-overview
 * drawer (Spec 356) build identical Create/Update inputs — no drift, especially in the
 * `updatedAt` optimistic-lock read inside `toUpdateInput`.
 */
export function toCreateInput(v: IqServiceUpsertValues): CreateIqServiceInventoryInput {
  return {
    iqServiceKey: v.iqServiceKey,
    ...(v.flowType ? { flowType: v.flowType } : {}),
    ...(v.bookingType ? { bookingType: v.bookingType } : {}),
    ...(v.displayName ? { displayName: v.displayName } : {}),
    ...(v.description ? { description: v.description } : {}),
    ...(v.internetqEmailRef ? { internetqEmailRef: v.internetqEmailRef } : {}),
    status: v.status,
  };
}

export function toUpdateInput(
  v: IqServiceUpsertValues,
  initial: IqServiceInventoryItem,
): UpdateIqServiceInventoryInput {
  // Partial update (Spec §6): a field is sent ONLY if it changed. A changed-to-empty
  // metadata field is sent as `null` (clear); unchanged fields are omitted (unchanged).
  const req: UpdateIqServiceInventoryInput = { updatedAt: initial.updatedAt };

  if (v.flowType !== (initial.flowType ?? null)) req.flowType = v.flowType;
  if (v.bookingType !== (initial.bookingType ?? null)) req.bookingType = v.bookingType;

  const displayName = v.displayName === '' ? null : v.displayName;
  if (displayName !== (initial.displayName ?? null)) req.displayName = displayName;

  const description = v.description === '' ? null : v.description;
  if (description !== (initial.description ?? null)) req.description = description;

  const emailRef = v.internetqEmailRef === '' ? null : v.internetqEmailRef;
  if (emailRef !== (initial.internetqEmailRef ?? null)) req.internetqEmailRef = emailRef;

  if (v.status !== initial.status) req.status = v.status;

  return req;
}
